<template>
  <div class="bom">
    <div class="bom__icon-wrap">
      <div
        class="bom__icon-bg"
        :style="{ background: accentBg }"
      >
        <i
          class="material-icons bom__icon"
          :style="{ color: accent }"
        >{{ glyph }}</i>
      </div>
    </div>

    <div class="bom__body">
      <h3 class="bom__title">{{ title }}</h3>
      <p class="bom__desc">
        Da próxima vez, entre direto {{ meansPhrase }} — suas credenciais ficam
        guardadas no dispositivo.
      </p>
    </div>

    <div class="bom__actions">
      <button class="bom__btn bom__btn--cancel" @click="onCancel">Agora não</button>
      <button
        class="bom__btn bom__btn--confirm"
        :style="{ background: accent, color: confirmText }"
        @click="onConfirm"
      >
        <i class="material-icons bom__btn-icon">{{ glyph }}</i>
        Ativar
      </button>
    </div>
  </div>
</template>

<script>
// Modal de opt-in pra biometria. Substitui o `window.confirm()` legado que
// tinha visual inconsistente, mensagem confusa, e ressurgia a cada login
// (sem "não perguntar de novo").
//
// Caller (Login.vue / DarkLogin.vue) abre via `$modal.show(BiometricOptInModal,
// { onConfirm, onCancel }, {...opts})`. onConfirm dispara o setCredentials;
// onCancel chama KeychainHandler.markOptInDismissed pra não re-perguntar.
//
// "Agora não" é tratado como "não, e não pergunta mais" (persistente). Pra
// re-habilitar, user precisa fazer login email+senha de novo OU tem que ir em
// Settings (futuro — pode adicionar toggle dedicado em Profile.vue).
export default {
  name: "BiometricOptInModal",
  props: {
    onConfirm: { type: Function, default: () => {} },
    onCancel: { type: Function, default: () => {} },
    // 'face' | 'fingerprint' | 'iris' | 'multiple' | null — de
    // KeychainHandler.getBiometryKind(). null cai no texto genérico, que é o
    // comportamento antigo: melhor vago que errado.
    biometryKind: { type: String, default: null }
  },
  computed: {
    // Dizer "Face ID" num Android com leitor de digital, ou "digital" num
    // iPhone com Face ID, é pior que ser genérico — o usuário procura na tela
    // uma coisa que não existe ali.
    title() {
      switch (this.biometryKind) {
        case 'face': return 'Entrar com reconhecimento facial?';
        case 'fingerprint': return 'Entrar com sua digital?';
        case 'iris': return 'Entrar com leitura da íris?';
        default: return 'Entrar com biometria?';
      }
    },
    meansPhrase() {
      switch (this.biometryKind) {
        case 'face': return 'com o reconhecimento facial';
        case 'fingerprint': return 'com sua digital';
        case 'iris': return 'com a leitura da íris';
        case 'multiple': return 'com a biometria do aparelho';
        default: return 'com sua digital ou Face ID';
      }
    },
    glyph() {
      if (this.biometryKind === 'face') return 'face';
      if (this.biometryKind === 'iris') return 'visibility';
      return 'fingerprint';
    },
    accent() {
      // Whitelabel: usa cor primária do tema do produtor
      return this.$theme.primaryColor || "#0564b1";
    },
    accentBg() {
      // Fundo translúcido derivado da cor primária. Hex → rgba com alpha 0.12.
      const hex = this.accent.replace("#", "");
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, 0.12)`;
    },
    confirmText() {
      // Em temas escuros com primary já claro, branco rende bem. Pra primaries
      // muito claras (raro), idealmente checaríamos contraste — por ora "#fff"
      // funciona pra todos os primaries reais do app.
      return "#fff";
    }
  }
};
</script>

<style scoped lang="scss">
// Modal sempre dark — login do app tem fundo escuro (Login.vue/DarkLogin.vue),
// e o modal aparece sobre ele. Tema light geraria contraste estranho.
.bom {
  padding: 28px 24px 24px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: #16161f;
  color: #f0f0f0;

  .bom__title { color: #f0f0f0; }
  .bom__desc { color: #6e6e82; }
  .bom__btn--cancel {
    color: #a0a0b0;
    border-color: rgba(255, 255, 255, 0.12);
    &:hover { background: rgba(255, 255, 255, 0.06); }
  }
}

.bom__icon-wrap {
  margin-bottom: 20px;
}

.bom__icon-bg {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bom__icon {
  font-size: 32px;
}

.bom__body {
  margin-bottom: 24px;
}

.bom__title {
  font-family: "Montserrat", Roboto, sans-serif;
  font-size: 17px;
  font-weight: 700;
  margin: 0 0 8px;
  line-height: 1.3;
}

.bom__desc {
  font-size: 13px;
  line-height: 1.55;
  margin: 0;
  padding: 0 4px;
}

.bom__actions {
  display: flex;
  gap: 10px;
  width: 100%;
}

.bom__btn {
  flex: 1;
  height: 44px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  font-family: "Montserrat", Roboto, sans-serif;
  cursor: pointer;
  border: none;
  outline: none;
  transition: opacity 0.15s ease, background 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;

  &:active { opacity: 0.75; }
}

.bom__btn-icon {
  font-size: 16px;
  line-height: 1;
}

.bom__btn--cancel {
  background: transparent;
  border: 1px solid;
}
</style>
